import {createFeatureSelector, createSelector} from '@ngrx/store';
import {DatevWizardState} from '../../+store/datev-wizard.state';
import {DatevWizardClientIdState} from './datev-wizard-client-id.state';
import {FullExportConfigDataDTO, SelectiveExportConfigDataDTO} from '../../../../../openapi/export-openapi';


export class DatevWizardClientIdSelectors {

  private static readonly _feature = createFeatureSelector<DatevWizardState>('wizard');

  private static readonly _slice = createSelector(
    this._feature,
    (state): DatevWizardClientIdState => state.datevWizardClientId,
  );

  public static readonly clientId = createSelector(
    this._slice,
    (state) => state.clientId,
  );

  public static readonly existingExportConfig = createSelector(
    this._slice,
    (state) => state.existingExportConfig,
  );

  public static readonly existingExportConfigId = createSelector(
    this._slice,
    (state) => state.existingExportConfig?.id,
  );

  public static readonly existingExportConfigStatus = createSelector(
    this._slice,
    (state) => state.existingExportConfigStatus,
  );

  /**
   * Selektor, um das Datum des bestehenden Exports zu erhalten. Es kann folgende Werte haben:
   * - `string`: Das Datum des Exports.
   * - `null`: Datum wurde bewusst nicht gesetzt.
   * - `undefined`: Kein Datum gesetzt, da der Payload-Typ das Datumsfeld nicht enthält.
   */
  public static readonly existingExportConfigPayloadDate = createSelector(
    this._slice,
    (state) => {
      const payload: FullExportConfigDataDTO | SelectiveExportConfigDataDTO | undefined = state.existingExportConfig?.payload;
      const type = payload?.type;

      let existingDate: string | null | undefined;

      switch (type) {
        case 'FullExportConfigData':
          const fullPayload = payload as FullExportConfigDataDTO;
          existingDate = fullPayload.exportSinceDocumentDate ?? null;
          break;
        case 'SelectiveExportConfigData':
          const selectivePayload = payload as SelectiveExportConfigDataDTO;
          break;
        default:
          break;
      }

      return existingDate;
    },
  );

  /**
   * Prüft, ob für eine bestimmte Firma/Client-Kombination gerade ein Config-Check läuft.
   */
  public static readonly isCheckingConfig = (firmenId: string, clientId: string) => createSelector(
    this._slice,
    (state) => {
      const key = `${firmenId}:${clientId}`;
      return state.checkConfigLoading.includes(key);
    },
  );

  /**
   * Prüft, ob für eine bestimmte Firma/Client-Kombination bereits ein Config-Check durchgeführt wurde.
   */
  public static readonly hasCheckedConfig = (firmenId: string, clientId: string) => createSelector(
    this._slice,
    (state) => {
      const key = `${firmenId}:${clientId}`;
      return state.checkConfigLoaded.includes(key);
    },
  );

  /**
   * Prüft, ob für eine bestimmte Export-Config-ID gerade die Konfiguration geladen wird.
   */
  public static readonly isLoadingExistingExportConfig = (exportConfigId: string) => createSelector(
    this._slice,
    (state) => state.existingExportConfigLoading.includes(exportConfigId),
  );

  /**
   * Prüft, ob für eine bestimmte Export-Config-ID die Konfiguration bereits geladen wurde.
   */
  public static readonly hasLoadedExistingExportConfig = (exportConfigId: string) => createSelector(
    this._slice,
    (state) => state.existingExportConfigLoaded.includes(exportConfigId),
  );

  /**
   * Prüft, ob für eine bestimmte Export-Config-ID gerade der Status geladen wird.
   */
  public static readonly isLoadingExistingExportConfigStatus = (exportConfigId: string) => createSelector(
    this._slice,
    (state) => state.existingExportConfigStatusLoading.includes(exportConfigId),
  );

  /**
   * Prüft, ob für eine bestimmte Export-Config-ID der Status bereits geladen wurde.
   */
  public static readonly hasLoadedExistingExportConfigStatus = (exportConfigId: string) => createSelector(
    this._slice,
    (state) => state.existingExportConfigStatusLoaded.includes(exportConfigId),
  );

  /**
   * Prüft, ob beim Laden des Export-Config-Status ein Fehler aufgetreten ist.
   */
  public static readonly hasExistingExportConfigStatusError = createSelector(
    this._slice,
    (state) => state.existingExportConfigStatusError,
  );

  /**
   * Prüft, ob beim Laden der Export-Config ein Fehler aufgetreten ist (außer 409/404).
   */
  public static readonly hasExistingExportConfigError = createSelector(
    this._slice,
    (state) => state.existingExportConfigError,
  );

  /**
   * Gibt den HTTP-Statuscode des letzten Export-Config-Fehlers zurück (z.B. 404, 403).
   */
  public static readonly existingExportConfigErrorStatus = createSelector(
    this._slice,
    (state) => state.existingExportConfigErrorStatus,
  );

  /**
   * Prüft, ob die Export-Config existiert (true bei 409, false bei 200/404).
   */
  public static readonly existingExportConfigExists = createSelector(
    this._slice,
    (state) => state.existingExportConfigExists,
  );

  /**
   * Prüft, ob für eine bestimmte Firma/Client-Kombination und optional Export-Config-ID
   * noch irgendwelche Ladevorgänge laufen.
   * Wird für die Navigation-Guards verwendet.
   */
  public static readonly isAnyLoadingInProgress = (firmenId: string, clientId: string, exportConfigId?: string) => createSelector(
    this._slice,
    (state) => {
      // Prüfe checkConfig Loading
      const checkConfigKey = `${firmenId}:${clientId}`;
      if (state.checkConfigLoading.includes(checkConfigKey)) {
        return true;
      }

      // Wenn exportConfigId vorhanden, prüfe auch die anderen Loading-States
      if (exportConfigId) {
        if (state.existingExportConfigLoading.includes(exportConfigId)) {
          return true;
        }
        if (state.existingExportConfigStatusLoading.includes(exportConfigId)) {
          return true;
        }
      }

      return false;
    },
  );

  /**
   * Gibt die loginUrl aus dem checkIfConfigExists Success-Response zurück.
   */
  public static readonly checkConfigSuccessLoginUrl = createSelector(
    this._slice,
    (state) => state.checkConfigSuccessLoginUrl,
  );

}
